import { SortState } from '@fox/data-grid';
import { useEffect, useState } from 'react';

export interface QueryHookProps<T> {
	requestToken: string;
	triggers?: string[];
	skip?: boolean;
	onFetchComplete?: (data: T) => void;
	onFetchCompleteAsync?: (data: T) => Promise<void>;
}

export interface QueryHookImplementationProps<T> extends QueryHookProps<T> {
	contentId: string;
}

export interface SwiftFoxEntitySummaryQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	useLinkedOrganisationData?: boolean;
}

export interface SwiftFoxFormDefinitionQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	formId: number;
}

export interface EventQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	eventId: string;
}
export interface ActionCentreSourcesQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	actionId: string;
}

export interface TableSettingsQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	tableNames?: string[];
}

export interface ActionCentreQueryHookImplementationProps<T> extends ActionCentreSourcesQueryHookImplementationProps<T> {
	page: number;
	pageSize: number;
	sourceFilter?: number;
}
export interface SubscriptionQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	subscriptionId: string;
}

export interface SubscriptionPayableLineItemsQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	subscriptionId: string;
}

export interface SubscriptionsPayableLineItemsQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	subscriptionIds: string[];
}

export interface RepresentativeRelationshipsQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	sfOrgId: string;
	representativeUnitId: string;
	page: number;
	pageSize: number;
	representativeOrgIds?: string[];
	search?: string;
}

export interface CustomFieldModelQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	type: string;
}

export interface ImportProfilesQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	category: string;
}

export interface VerifyImportQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	id: string;
	numberOfRecordsImported: number;
	organisationId: string;
	personId?: string;
}

export interface EventsQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	search?: string;
	startDate?: string;
	endDate?: string;
	page?: number;
	pageSize?: number;
	rsvpOptions?: string[];
	locations?: string[];
	states?: string[];
}

export interface EntitySearchQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	searchString?: string;
	types?: string[];
}

export interface ReceiptsQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	sort?: SortState;
	page?: number;
	pageSize?: number;
	useLinkedOrganisationData?: boolean;
	allowedProviders?: string[];
	/** The id of the receipt replacing the receipts we want to find */
	replacedBy?: string;
	findFailed?: boolean;
}

export interface ReceiptQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	id: string;
}

export interface InvoicesQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	sort?: SortState;
	page?: number;
	pageSize?: number;
	useLinkedOrganisationData?: boolean;
}

export interface InvoiceQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	invoiceId: number;
}

export interface SubscriptionsQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	useLinkedOrganisationData?: boolean;
}

export interface EditRulesQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	useLinkedOrganisationData?: boolean;
}

export interface CustomerQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	useLinkedOrganisationData?: boolean;
}

export interface QueryHookTemplateProps<T> extends QueryHookProps<T> {
	url: string;
}

export interface GoalsQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	page: number;
	pageSize: number;
	useLinkedOrganisationData?: boolean;
}

export interface GoalsByOwnerTypeQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	useLinkedOrganisationData?: boolean;
}

export interface GoalPointsCategoriesQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	useLinkedOrganisationData?: boolean;
}

export interface GoalsActivitiesQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	page: number;
	pageSize: number;
	useLinkedOrganisationData?: boolean;
}

export interface GoalsSnapshotQueryHookImplementationProps<T> extends QueryHookImplementationProps<T> {
	useLinkedOrganisationData?: boolean;
}
export function useQuery<T>(input: QueryHookTemplateProps<T>): { loading: boolean; refetch: () => Promise<T>; data?: T; error?: string } {
	const { requestToken, skip, url, triggers, onFetchComplete, onFetchCompleteAsync } = input;
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [data, setData] = useState<T>(undefined);

	const fetchData = async () => {
		if (!skip) {
			try {
				setLoading(true);
				setError('');
				const res = await fetch(url, {
					method: 'GET',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
						RequestVerificationToken: requestToken
					}
				});
				if (res.ok) {
					const response = await res.json();
					if (response.success === true) {
						setData(response.result);
						setLoading(false);
						onFetchComplete?.(response.result);
						if (onFetchCompleteAsync) {
							await onFetchCompleteAsync(response.result);
						}
						return response.result;
					} else {
						const err = response.message;
						setLoading(false);
						setError(err);
					}
				} else {
					const err = await res.text();
					setLoading(false);
					setError(err);
				}
			} catch (e) {
				setLoading(false);
				setError('Error: Something went wrong with the query');
			}
		}
	};

	useEffect(
		() => {
			fetchData();
			return () => {
				setError('');
				setLoading(false);
				setData(undefined);
			};
		},
		triggers?.length ? [...triggers?.map(t => input[t])] : []
	);

	const refetch = () => fetchData();

	return { error, data, loading, refetch };
}
